import { Callout, HeaderText, Icons } from "@/components/ui";
import { Sidebar } from "@/components/ui/Sidebar/Sidebar";
import {
  getPendingAiFeedItemCount,
  useAiFeed,
} from "@/features/AiFeed/network/useAiFeed";
import { ErrorBoundary } from "@/lib/monitoring";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import type { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
import {
  Outlet,
  useLocation,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import {
  Activity,
  Ambulance,
  Baby,
  BookHeart,
  Calendar,
  CircleDollarSign,
  ClipboardList,
  Contact,
  Eye,
  FolderOpen,
  Hospital,
  House,
  Network,
  Pill,
  ShieldHalf,
  Slice,
  Sparkles,
  Syringe,
  TestTube,
  TriangleAlert,
  Trophy,
  Users,
} from "lucide-react";
import { useEffect, useRef } from "react";
import {
  getUnActionedAVCallHistoricalCount,
  useAVCallSuggestedConditions,
} from "./MedicalHistory/network";

interface NavRoute {
  icon: React.ElementType;
  label: string;
  to: string;
  indicator?: boolean;
}

const baseRoutes: NavRoute[] = [
  {
    icon: Sparkles,
    label: "AI Feed",
    to: "ai-feed",
  },
  {
    icon: Calendar,
    label: "Timeline",
    to: "timeline",
  },
  {
    icon: Eye,
    label: "Quick View",
    to: "quick-view",
  },
  {
    icon: House,
    label: "Profile",
    to: "profile",
  },
  {
    icon: Activity,
    label: "Vitals",
    to: "vitals",
  },
  {
    icon: Hospital,
    label: "Medical History",
    to: "medical-history",
  },
  {
    icon: Pill,
    label: "Medications",
    to: "medications-allergies",
  },
  // add separator here
  {
    icon: Users,
    label: "Social History",
    to: "social-history",
  },
  {
    icon: BookHeart,
    label: "Sexual History",
    to: "sexual-history",
  },
  {
    icon: Baby,
    label: "Family History",
    to: "family-history",
  },
  {
    icon: Icons.WomensHealthLogo,
    label: "Women's Health",
    to: "womens-health",
  },
  // add separator here
  {
    icon: Ambulance,
    label: "Hospitalizations",
    to: "hospitalizations",
  },
  {
    icon: Syringe,
    label: "Immunizations",
    to: "immunizations",
  },
  {
    icon: TestTube,
    label: "Labs",
    to: "labs",
  },
  {
    icon: Slice,
    label: "Surgical History",
    to: "surgical-history",
  },
  //add separator here
  {
    icon: ShieldHalf,
    label: "Health Maintenance",
    to: "health-maintenance",
  },
  {
    icon: Trophy,
    label: "Goals",
    to: "goals",
  },
  {
    icon: CircleDollarSign,
    label: "Health Insurance",
    to: "health-insurance",
  },
  {
    icon: Network,
    label: "HIE Data",
    to: "hie-data",
  },
  {
    icon: Contact,
    label: "External Care Team",
    to: "external-care-team",
  },
  {
    icon: FolderOpen,
    label: "Files",
    to: "files",
  },
  {
    icon: ClipboardList,
    label: "Work Notes",
    to: "work-notes",
  },
  {
    icon: TriangleAlert,
    label: "Alerts",
    to: "alerts",
  },
];

export const MedicalRecordsContainer = () => {
  const { pathname } = useLocation();
  const errorBoundaryRef = useRef<SentryErrorBoundary | null>(null);
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId: encounterIdRaw } = useSearch({
    from: "/patient/$patientId",
  });
  // Route schema declares encounterId as z.union([z.number(), z.string()]);
  // normalize once so the query key + downstream string compares are safe.
  const encounterId = encounterIdRaw?.toString();

  const { data: aiFeedEnabled } = useConfigCatFeatureFlag(
    "ehr-ai-feed-enabled",
  );
  const showAiFeed = Boolean(aiFeedEnabled && encounterId);
  const { data: aiFeedItems } = useAiFeed(patientId, encounterId, {
    enabled: showAiFeed,
  });
  const hasAiFeedNeedsReview = getPendingAiFeedItemCount(aiFeedItems) > 0;

  // Drives the small dot indicator on the Medical History sidebar icon so the
  // provider knows there are unactioned AV-call condition suggestions waiting
  // to be reviewed — even when they're on a different sub-route (Timeline,
  // Profile, etc.) within the MedicalRecords panel.
  const { data: avCallSuggestions } = useAVCallSuggestedConditions({
    patientId,
    encounterId,
  });
  const hasAVCallSuggestions =
    !aiFeedEnabled && getUnActionedAVCallHistoricalCount(avCallSuggestions) > 0;

  const { data: isMetriportPatientDataIframeEnabled } = useConfigCatFeatureFlag(
    "metriport-patient-data-iframe",
  );

  const routes: NavRoute[] = baseRoutes
    .filter((r) => r.to !== "ai-feed" || showAiFeed)
    .filter(
      (r) =>
        r.to !== "hie-data" || Boolean(isMetriportPatientDataIframeEnabled),
    )
    .map((r) => {
      if (r.to === "ai-feed") {
        return { ...r, indicator: hasAiFeedNeedsReview };
      }
      if (r.to === "medical-history") {
        return { ...r, indicator: hasAVCallSuggestions };
      }
      return r;
    });

  useEffect(() => {
    if (pathname) {
      // Ensure the boundary is cleared immediately on subroute changes
      errorBoundaryRef.current?.resetErrorBoundary();
    }
  }, [pathname]);
  return (
    <div className="w-full min-h-0 flex flex-col min-w-[33%] max-w-[50%]">
      <div className="flex items-center justify-between p-3 border border-b-0 rounded-t-md">
        <div className="flex items-center gap-2">
          <HeaderText textColor="tertiary" size="sm">
            Medical Records
          </HeaderText>
        </div>
      </div>
      <div className="flex h-full min-h-0 border rounded-b">
        <Sidebar routes={routes} />
        <div className="p-3 pb-0 w-full overflow-y-auto">
          <ErrorBoundary
            key={`${pathname}`}
            ref={errorBoundaryRef}
            fallback={
              <Callout
                variant="danger"
                title="An error has occurred loading the requested medical records"
                description="Please refresh the page. If the problem persists, please use the feedback tool to report the issue."
              />
            }
          >
            <Outlet />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};
